import { clsxMerge } from '@dreamstack/util'
import type { PropertyImage } from '@dreamstack/web/lib/prisma/properties'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import { FadeIn } from '../layout/FadeIn'
import { GridWithGaps, MainContainer } from '../layout/MainGrid'
import { Css_ImageHeadingPropProject_tw } from '../layout/Texts'
import {
  ColoredGradient,
  TransparentGradientTopToBottom,
} from '../layout/Gradients'
import { transformPropstackImageUrl } from './propstack-image-url-transform'

const HeaderContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={clsxMerge(
      'w-full relative flex-shrink-0 h-[619px] md:h-[485px] ml:h-[710px]',
      props.className
    )}
  />
)

const HeaderImageContainer = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={clsxMerge(
      'w-full h-full absolute overflow-hidden -z-[1]',
      props.className
    )}
  />
)

const TransparentSpaceBlocker = (
  props: React.HTMLAttributes<HTMLDivElement>
) => (
  <div
    {...props}
    className={clsxMerge(
      'w-full flex-shrink-0 bg-transparent -mt-px-96 md:-mt-px-128 lg:-mt-px-196',
      props.className
    )}
  />
)

const HeaderTextContainer = ({
  objectHasNoImage,
  className,
  ...rest
}: React.HTMLAttributes<HTMLDivElement> & { objectHasNoImage: boolean }) => (
  <div
    {...rest}
    className={clsxMerge(
      'h-full whitespace-pre-wrap text-white md:mt-px-96',
      objectHasNoImage && 'text-accentroGray-full',
      className
    )}
  />
)

const HeaderTextPlacement = (props: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    {...props}
    className={clsxMerge(
      'flex flex-col justify-center h-full break-words overflow-clip col-start-1 col-span-12 lg:my-px-48',
      props.className
    )}
  />
)

const HeaderImageText = ({
  smallFont,
  characterCount,
  className,
  ...rest
}: React.HTMLAttributes<HTMLHeadingElement> & {
  smallFont?: boolean
  characterCount: number
}) => (
  <h1
    {...rest}
    className={clsxMerge(
      Css_ImageHeadingPropProject_tw,
      smallFont && 'text-px-32 leading-px-44',
      characterCount > 80 &&
        'text-px-28 leading-[40px] sm:text-px-32 sm:leading-px-44 ml:text-px-72 ml:leading-px-80',
      className
    )}
    aria-live="polite"
  />
)

export const HeaderImage: FunctionComponent<
  React.PropsWithChildren<{
    title: string
    image?: Pick<PropertyImage, 'title' | 'url' | 'id'>
    smallFont?: boolean
    isObject?: boolean
    characterCount?: number
  }>
> = ({ image, title, smallFont, isObject, characterCount }) => {
  const imageUrl = !!image
    ? transformPropstackImageUrl({ url: image.url, size: 'default' })
    : ''
  const objectHasNoImage = !!isObject && !image
  return (
    <div>
      <TransparentSpaceBlocker />
      <HeaderContainer>
        <HeaderImageContainer>
          {!!imageUrl && (
            <>
              <Image
                fill
                alt={image?.title ?? 'property image'}
                src={imageUrl}
                priority={true}
                style={{ objectFit: 'cover' }}
              />
              {<TransparentGradientTopToBottom />}
            </>
          )}
          {!image && <ColoredGradient />}
          {!!objectHasNoImage && (
            <div className="absolute inset-0 bg-accentroAquaVeryLight-full"></div>
          )}
        </HeaderImageContainer>
        <HeaderTextContainer objectHasNoImage={objectHasNoImage}>
          <MainContainer className="h-full">
            <GridWithGaps className="h-full">
              <HeaderTextPlacement>
                <FadeIn>
                  <HeaderImageText
                    characterCount={characterCount ?? 0}
                    smallFont={smallFont}
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                </FadeIn>
              </HeaderTextPlacement>
            </GridWithGaps>
          </MainContainer>
        </HeaderTextContainer>
      </HeaderContainer>
    </div>
  )
}
